import { useEffect, useState } from "react";
import { Sparkles } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import { Spinner } from "@/components/ui/Spinner";
import { FileDropZone } from "@/components/ui/FileDropZone";
import { useMpzpOrderMutations, useTaskPolling } from "../../hooks";
import type { MpzpOrder } from "../../types";
import { AttachExistingFeeField } from "../DooFeeAttach";

interface FeeLetterResult {
  found?: boolean;
  amount?: string;
  account_number?: string;
  payment_title?: string;
  doo_number?: string;
  case_sign?: string;
  office_name?: string;
  office_address?: string;
}

/**
 * Prośba o opłatę za wypis i wyrys.
 *
 * Kwoty nie znamy z góry — wylicza ją gmina (liczba stron wypisu, liczba arkuszy A4
 * wyrysu) i przysyła pismem. Wgrany dokument czyta AI, ale — inaczej niż przy wypisach
 * EGiB — W TLE: to wywołanie na PDF-ie i potrafi trwać kilkanaście sekund, więc modal
 * odpytuje zadanie zamiast blokować request.
 */
export function MpzpRequestFeeModal({
  order,
  onClose,
}: {
  order: MpzpOrder;
  onClose: () => void;
}) {
  const { requestFee, uploadFeeLetter } = useMpzpOrderMutations(order.id);
  const [amount, setAmount] = useState("");
  const [account, setAccount] = useState(order.suggested_account_number || "");
  const [transferName, setTransferName] = useState(
    `MPZP ${order.scope_label} — ${order.number}`,
  );
  const [description, setDescription] = useState("");
  const [error, setError] = useState("");
  const [warning, setWarning] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [taskId, setTaskId] = useState<string | null>(null);
  const [manual, setManual] = useState(false);
  const [existingFee, setExistingFee] = useState("");

  const { data: task } = useTaskPolling(taskId);
  const reading = !!taskId && !["success", "failure", "cancelled"].includes(task?.status ?? "");

  useEffect(() => {
    if (!task || reading) return;
    if (task.status === "failure") {
      setWarning("Nie udało się odczytać pisma — wypełnij pola ręcznie.");
      setManual(true);
      setTaskId(null);
      return;
    }
    if (task.status !== "success") return;
    const r = (task.result ?? {}) as FeeLetterResult;
    if (r.amount) setAmount(r.amount);
    if (r.account_number) setAccount(r.account_number);
    // Urząd wymaga tytułu przelewu DOKŁADNIE takiego, jak w piśmie — jeśli dokument go
    // podaje, przepisujemy bez dopisków (nasz numer zamówienia idzie do opisu).
    if (r.payment_title) setTransferName(r.payment_title);
    const parts = [
      r.doo_number ? `pismo ${r.doo_number}` : "",
      r.case_sign ? `znak sprawy: ${r.case_sign}` : "",
      `zamówienie: ${order.number}`,
    ].filter(Boolean);
    setDescription(parts.join(" · "));
    if (!r.found) {
      setWarning("Nie udało się odczytać kwoty z pisma — uzupełnij ją ręcznie.");
    }
    setManual(true);
    setTaskId(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task?.status]);

  const upload = async (file: File) => {
    setWarning("");
    try {
      const { task_id } = await uploadFeeLetter.mutateAsync({ id: order.id, file });
      setTaskId(task_id);
    } catch {
      setWarning("Nie udało się wgrać pisma — wypełnij pola ręcznie.");
      setManual(true);
    }
  };

  const submit = () => {
    setError("");
    requestFee.mutate(
      {
        id: order.id,
        payload: existingFee
          ? { mode: "existing" as const, fee: existingFee }
          : {
              amount,
              office_account_number: account,
              transfer_name: transferName || undefined,
              description: description || undefined,
            },
      },
      {
        onSuccess: onClose,
        onError: (e: unknown) => {
          const detail = (e as { response?: { data?: { errors?: { detail?: unknown } } } })
            ?.response?.data?.errors?.detail;
          setError(
            typeof detail === "string"
              ? detail
              : detail
                ? JSON.stringify(detail)
                : "Nie udało się złożyć prośby.",
          );
        },
      },
    );
  };

  return (
    <Modal open onClose={onClose} title={`Prośba o opłatę — ${order.number}`}>
      <div className="space-y-3">
        <div className="text-xs text-[var(--color-text-secondary)]">
          Prośba trafi do <b>/official-fees</b> normalnym obiegiem: akceptacja
          dyrektora/koordynatora, potem opłacenie z konta podręcznego. Urząd:{" "}
          <b>{order.office_name}</b>.
        </div>

        <div className="space-y-2">
          <div className="flex items-center gap-2 text-xs">
            <Sparkles size={14} className="shrink-0" />
            <span>
              <b>Pismo gminy o wysokości opłaty</b> — kwota, konto i tytuł uzupełnią się
              automatycznie
            </span>
          </div>
          <FileDropZone
            files={files}
            accept=".pdf"
            onFilesChange={(fs) => {
              setFiles(fs);
              if (fs[0]) upload(fs[0]);
            }}
          />
          {reading && (
            <div className="flex items-center gap-2 text-xs text-[var(--color-text-muted)]">
              <Spinner /> Czytam pismo…
            </div>
          )}
          {warning && <div className="text-xs text-amber-600">{warning}</div>}
        </div>

        <AttachExistingFeeField module="mpzp" value={existingFee} onChange={setExistingFee} />

        {existingFee ? (
          <>
            {error && <div className="text-xs text-red-600">{error}</div>}
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={onClose}>Anuluj</Button>
              <Button onClick={submit} loading={requestFee.isPending}>
                Podepnij opłatę
              </Button>
            </div>
          </>
        ) : !manual ? (
          <div className="text-xs text-[var(--color-text-muted)] py-2 space-y-2">
            <p>
              {reading
                ? "Trwa odczyt pisma — pola opłaty pojawią się po jego zakończeniu."
                : "Wgraj pismo gminy o wysokości opłaty. Jeśli jeszcze go nie masz, możesz wpisać kwotę ręcznie."}
            </p>
            {!reading && (
              <Button size="sm" variant="secondary" onClick={() => setManual(true)}>
                Wpisz ręcznie
              </Button>
            )}
          </div>
        ) : (
          <>
            <label className="block">
              <span className="section-label">Kwota [PLN] *</span>
              <input
                className="input-base mt-1"
                type="number"
                min="0"
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
              />
            </label>
            <label className="block">
              <span className="section-label">
                Numer konta urzędu *{" "}
                {order.suggested_account_number ? "(podpowiedź: ostatnio użyte)" : ""}
              </span>
              <input
                className="input-base mt-1 font-mono"
                value={account}
                placeholder="26 cyfr (PL…)"
                onChange={(e) => setAccount(e.target.value)}
              />
            </label>
            <label className="block">
              <span className="section-label">Tytuł przelewu</span>
              <input
                className="input-base mt-1"
                value={transferName}
                onChange={(e) => setTransferName(e.target.value)}
              />
            </label>
            <label className="block">
              <span className="section-label">Opis (opcjonalnie)</span>
              <textarea
                className="input-base mt-1 h-14"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </label>
            {error && <div className="text-xs text-red-600">{error}</div>}
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={onClose}>Anuluj</Button>
              <Button onClick={submit} loading={requestFee.isPending} disabled={!amount || !account}>
                Złóż prośbę o opłatę
              </Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
